import {
  Dispatch,
  FunctionComponent,
  memo,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { organisationApi, OrganisationUserManagementStatus, UserRole } from '../../../../services';
import {
  Button,
  ButtonType,
  Check,
  Modal,
  Radio,
  Select,
  useAppSelector,
} from '../../../../shared';

import classes from './AddUsersInChannelModal.module.scss';

interface AddUsersInChannelModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  userIds: number[];
  showNotifyUsers: boolean;
  callback: () => void;
}

export const AddUsersInChannelModal: FunctionComponent<AddUsersInChannelModalProps> = memo(
  ({ isOpen, setIsOpen, userIds, showNotifyUsers, callback }) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'orgDashboardUsersInviteModal' });

    const [organisationUserInviteByUserId] =
      organisationApi.endpoints.organisationUserInviteByUserId.useMutation();

    const { channels } = useAppSelector(({ channels }) => channels);

    const [channelIds, setChannelIds] = useState<number[]>([]);

    const [role, setRole] = useState<UserRole>(UserRole.CHANNEL_READER);

    const [notifyExistingUsers, setNotifyExistingUsers] = useState<boolean>(false);

    const [loading, setLoading] = useState<boolean>(false);

    const userIdsLength = useMemo(() => userIds.length, [userIds.length]);

    const channelDropdownItems = useMemo(
      () =>
        channels.map(({ id, name }) => {
          return { id, title: name };
        }),
      [channels]
    );

    const onClose = useCallback(() => {
      setIsOpen(false);
      setNotifyExistingUsers(false);
      setChannelIds([]);
      setRole(UserRole.CHANNEL_READER);
    }, [setIsOpen]);

    const inviteToChannelHandler = useCallback(async () => {
      setLoading(true);

      try {
        const userManagementResult = await organisationUserInviteByUserId({
          channelIds,
          userIds,
          role,
          notifyExistingUsers,
        }).unwrap();

        if (userManagementResult.length) {
          const hasSkipOrAccess = userManagementResult.find(({ status }) =>
            [
              OrganisationUserManagementStatus.SKIP,
              OrganisationUserManagementStatus.ACCESS,
            ].includes(status)
          );

          hasSkipOrAccess ? toast.info(ctxT('invite-skip')) : toast.success(ctxT('invite-success'));

          callback();
          onClose();
          return;
        }

        toast.error(t('common.error-message'));
      } catch (_) {
        toast.error(t('common.error-message'));
      } finally {
        setLoading(false);
      }
    }, [
      callback,
      channelIds,
      ctxT,
      notifyExistingUsers,
      onClose,
      organisationUserInviteByUserId,
      role,
      t,
      userIds,
    ]);

    const title = ctxT('add-users-to-channel', { count: userIdsLength });

    const body = useMemo(() => {
      return (
        <div className={classes['add-users']}>
          <div className={classes['add-users__title']}>{title}</div>

          <div className={classes['add-users__role']}>
            <div className={classes['add-users__role-label']}>{t('common.role')}</div>
            {Object.values(UserRole)
              .filter((value) => value.startsWith('CHANNEL_'))
              .map((value) => {
                return (
                  <Radio
                    key={value}
                    checked={value === role}
                    name={'channel-role'}
                    label={t(`userRoles.${value}`)}
                    onChange={() => setRole(value)}
                  />
                );
              })}
          </div>

          <Select
            label={t('common.channel')}
            selectedItemIds={channelIds}
            items={channelDropdownItems}
            onMultiCheckChange={setChannelIds}
          />

          {showNotifyUsers && (
            <Check
              label={t('orgDashboardUsers.notify-new-channel-user')}
              checked={notifyExistingUsers}
              onChange={setNotifyExistingUsers}
            />
          )}

          <Button
            type={ButtonType.primary}
            label={ctxT('add-users', { count: userIdsLength })}
            className={classes['add-users__button']}
            onClick={inviteToChannelHandler}
            disabled={Boolean(loading || !channelIds.length)}
            loading={loading}
          />
        </div>
      );
    }, [
      channelDropdownItems,
      channelIds,
      ctxT,
      inviteToChannelHandler,
      loading,
      notifyExistingUsers,
      role,
      showNotifyUsers,
      t,
      title,
      userIdsLength,
    ]);

    if (!userIdsLength) {
      return null;
    }

    return (
      <Modal
        isOpen={isOpen}
        body={body}
        onClose={onClose}
        ariaLabel={title}
        alignTop
        disableCloseByOverlay
      />
    );
  }
);
